import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Tab, Tabs, Card, Badge, Alert, ListGroup, Image } from 'react-bootstrap';
import { faker } from '@faker-js/faker';
import toast from 'react-hot-toast';
import ImageUploadField from '../../components/Common/ImageUploadField';
import { IMAGE_SPECS } from '../../utils/imageSpecs';
import { adminService } from '../../services/adminService';

interface MovieDetailsModalProps {
  show: boolean;
  onHide: () => void;
  onSave: (movieData: any) => void;
  movie?: any;
  mode: 'view' | 'edit' | 'add';
}

const ALLOWED_GENRES = [
  'Action',
  'Comedy',
  'Drama',
  'Thriller',
  'Horror',
  'Romance',
  'Sci-Fi',
  'Fantasy',
  'Sports',
];


const MovieDetailsModal: React.FC<MovieDetailsModalProps> = ({
  show,
  onHide,
  onSave,
  movie,
  mode
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    genre: [],
    duration: '',
    rating: 'U',
    language: 'English',
    releaseDate: '',
    posterUrl: '',
    trailerUrl: '',
    cast: '',
    director: '',
    status: 'Now Showing',
    isActive: true,
    metaTitle: '',
    metaDescription: '',
    tags: '',
    ageRestriction: 'U',
    subtitles: false,
    dubbing: false,
    imdbRating: '',
    rottenTomatoesRating: '',
    budget: '',
    boxOffice: '',
    awards: '',
    runtime: '',
    aspectRatio: '16:9',
    color: 'Color',
    soundMix: 'Dolby Digital',
    cinematography: '',
    editing: '',
    music: '',
    productionDesign: '',
    costumeDesign: '',
    makeup: '',
    specialEffects: '',
    stunts: '',
    animalWork: '',
    otherCrew: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [activeTab, setActiveTab] = useState('basic');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingPoster, setUploadingPoster] = useState(false);

  useEffect(() => {
    if (movie && mode !== 'add') {
      setFormData({
        title: movie.title || '',
        description: movie.description || '',
        genre: Array.isArray(movie.genre)
        ? movie.genre
        : typeof movie.genre === 'string' && movie.genre
        ? movie.genre.replace(/[{}"\\]/g, '').split(',').map((g: string) => g.trim()).filter(Boolean)
        : [],
        duration: movie.duration?.toString() || '',
        rating: movie.rating || 'U',
        language: movie.language || 'English',
        releaseDate: movie.releaseDate || '',
        posterUrl: movie.posterUrl || '',
        trailerUrl: movie.trailerUrl || '',
        cast: movie.cast || '',
        director: movie.director || '',
        status: movie.status || 'Now Showing',
        isActive: movie.isActive !== undefined ? movie.isActive : true,
        metaTitle: movie.metaTitle || '',
        metaDescription: movie.metaDescription || '',
        tags: movie.tags || '',
        ageRestriction: movie.ageRestriction || 'U',
        subtitles: movie.subtitles || false,
        dubbing: movie.dubbing || false,
        imdbRating: movie.imdbRating || '',
        rottenTomatoesRating: movie.rottenTomatoesRating || '',
        budget: movie.budget || '',
        boxOffice: movie.boxOffice || '',
        awards: movie.awards || '',
        runtime: movie.runtime || '',
        aspectRatio: movie.aspectRatio || '16:9',
        color: movie.color || 'Color',
        soundMix: movie.soundMix || 'Dolby Digital',
        cinematography: movie.cinematography || '',
        editing: movie.editing || '',
        music: movie.music || '',
        productionDesign: movie.productionDesign || '',
        costumeDesign: movie.costumeDesign || '',
        makeup: movie.makeup || '',
        specialEffects: movie.specialEffects || '',
        stunts: movie.stunts || '',
        animalWork: movie.animalWork || '',
        otherCrew: movie.otherCrew || ''
      });
    } else if (mode === 'add') {
      // Reset form for new movie
      setFormData({
        title: '',
        description: '',
        genre: [],
        duration: '',
        rating: 'U',
        language: 'English',
        releaseDate: '',
        posterUrl: '',
        trailerUrl: '',
        cast: '',
        director: '',
        status: 'Now Showing',
        isActive: true,
        metaTitle: '',
        metaDescription: '',
        tags: '',
        ageRestriction: 'U',
        subtitles: false,
        dubbing: false,
        imdbRating: '',
        rottenTomatoesRating: '',
        budget: '',
        boxOffice: '',
        awards: '',
        runtime: '',
        aspectRatio: '16:9',
        color: 'Color',
        soundMix: 'Dolby Digital',
        cinematography: '',
        editing: '',
        music: '',
        productionDesign: '',
        costumeDesign: '',
        makeup: '',
        specialEffects: '',
        stunts: '',
        animalWork: '',
        otherCrew: ''
      });
    }
  }, [movie, mode]);

  useEffect(() => {
    if (show) {
      setActiveTab('basic');
    }
  }, [show, movie]);



  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };


  const validateForm = (): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    // Title - Commented out validation since title field is disabled
    // if (!formData.title.trim()) {
    //   newErrors.title = 'Movie title is required';
    // }

    // Duration - intentionally optional, no validation enforced

    // Genre
    if (!Array.isArray(formData.genre) || formData.genre.length === 0) {
      newErrors.genre = 'At least one genre is required';
    }

    // Language
    if (!formData.language) {
      newErrors.language = 'Language is required';
    }

    // Status
    if (!formData.status) {
      newErrors.status = 'Status is required';
    }

    // Release Date
    if (!formData.releaseDate) {
      newErrors.releaseDate = 'Release date is required';
    }

    return newErrors;
  };


  const isFutureDate = (date: string) => {
    if (!date) return false;

    const todayStr = new Date().toISOString().split('T')[0];
    return date > todayStr; // string comparison (YYYY-MM-DD)
  };


  const handleSave = async () => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const tabLabels: Record<string, string> = {
        basic: 'Basic Info',
        media: 'Media & Links',
        technical: 'Technical Details',
        crew: 'Cast & Crew',
        ratings: 'Ratings & Awards',
        seo: 'SEO & Meta',
      };
      const targetTab = 'basic';
      setActiveTab(targetTab);
      const summary = Object.entries(newErrors)
        .map(([, msg]) => `${msg} (${tabLabels[targetTab]})`)
        .join(' · ');
      toast.error(`Please fix: ${summary}`, { duration: 6000 });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        ...formData,
        duration: Number(formData.duration),
      };

      await onSave(payload);
      onHide();
      setErrors({});
    } catch (error) {
      console.error('Error saving movie:', error);
    } finally {
      setIsLoading(false);
    }
  };




  const uploadPoster = async (file: File): Promise<string | null> => {
    setUploadingPoster(true);
    try {
      const response = await adminService.uploadMoviePoster(file);
      if (response.success && response.data?.url) {
        return response.data.url;
      }
      throw new Error(response.error || "Upload failed");
    } finally {
      setUploadingPoster(false);
    }
  };

  const generatePosterUrl = () => {
    const newUrl = faker.image.url({ width: 300, height: 450 });
    handleInputChange('posterUrl', newUrl);
  };

  const generateTrailerUrl = () => {
    const trailerUrls = [
      'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      'https://www.youtube.com/watch?v=9bZkp7q19f0',
      'https://www.youtube.com/watch?v=kJQP7kiw5Fk',
      'https://www.youtube.com/watch?v=fJ9rUzIMcZQ'
    ];
    const randomIndex = Math.floor(Math.random() * trailerUrls.length);
    const randomUrl = trailerUrls[randomIndex];
    handleInputChange('trailerUrl', randomUrl);
  };

  const isReadOnly = mode === 'view';

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {mode === 'add' && 'Add New Movie'}
          {mode === 'edit' && 'Edit Movie'}
          {mode === 'view' && 'Movie Details'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'basic')} variant="pills" className="mb-3">
          <Tab eventKey="basic" title="Basic Info">
            <Card className="mt-3">
              <Card.Header>
                <h5 className="mb-0">Basic Information</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={8}>
                    <Form.Group className="mb-3">
                      <Form.Label>Movie Title *</Form.Label>
                      {/* Movie title field disabled - used for duplicate detection */}
                      <Form.Control
                        type="text"
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        isInvalid={!!errors.title}
                        readOnly={isReadOnly}
                        disabled={true}
                        title="Movie title cannot be edited - used for duplicate detection"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.title}
                      </Form.Control.Feedback>


                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Status</Form.Label>


                      <Form.Select
                        value={formData.status}
                        onChange={(e) => handleInputChange('status', e.target.value)}
                        disabled={isReadOnly}
                      >
                        <option value="Now Showing" disabled={isFutureDate(formData.releaseDate)}>
                          Now Showing
                        </option>
                        <option value="Coming Soon">Coming Soon</option>
                        <option value="Ended" disabled={isFutureDate(formData.releaseDate)}>
                          Ended
                        </option>
                      </Form.Select>



                    </Form.Group>
                  </Col>
                </Row>





                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  {/* Description field disabled - used for content consistency */}
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Enter movie description"
                    readOnly={isReadOnly}
                    disabled={true}
                    title="Description cannot be edited - used for content consistency"
                  />
                </Form.Group>

                <Row>
                  <Col md={3}>
              
                  <Form.Group className="mb-3">
  <Form.Label>Genre</Form.Label>
  <div
    style={{
      border: errors.genre ? '1px solid #dc3545' : '1px solid #dee2e6',
      borderRadius: '6px',
      backgroundColor: isReadOnly ? '#e9ecef' : '#fff',
      padding: '8px',
    }}
  >
    {/* Selected tags on top */}
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', minHeight: '32px', marginBottom: '8px' }}>
      {(formData.genre as string[]).length === 0 && (
        <span style={{ color: '#adb5bd', fontSize: '0.875rem', padding: '4px 2px' }}>
          No genres selected
        </span>
      )}
      {(formData.genre as string[]).map((g) => (
        <span
          key={g}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            backgroundColor: '#0d6efd',
            color: '#fff',
            borderRadius: '20px',
            padding: '3px 10px',
            fontSize: '0.8rem',
            fontWeight: 500,
          }}
        >
          {g}
          {!isReadOnly && (
            <span
              onClick={() => {
                const updated = (formData.genre as string[]).filter((x) => x !== g);
                handleInputChange('genre', updated);
              }}
              style={{ cursor: 'pointer', marginLeft: '2px', fontWeight: 'bold', fontSize: '0.9rem' }}
            >
              ×
            </span>
          )}
        </span>
      ))}
    </div>

    {/* Divider */}
    {!isReadOnly && <hr style={{ margin: '6px 0' }} />}

    {/* Available genres to pick */}
    {!isReadOnly && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {ALLOWED_GENRES.filter((g) => !(formData.genre as string[]).includes(g)).map((genre) => (
          <span
            key={genre}
            onClick={() => handleInputChange('genre', [...(formData.genre as string[]), genre])}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              backgroundColor: '#f8f9fa',
              color: '#495057',
              border: '1px solid #dee2e6',
              borderRadius: '20px',
              padding: '3px 10px',
              fontSize: '0.8rem',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            + {genre}
          </span>
        ))}
      </div>
    )}
  </div>
  {errors.genre && (
    <div style={{ fontSize: '0.875em', color: '#dc3545', marginTop: '4px' }}>
      {errors.genre}
    </div>
  )}
</Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Rating</Form.Label>
                      {/* Rating field disabled - used for content consistency */}
                      <Form.Select
                        value={formData.rating}
                        onChange={(e) => handleInputChange('rating', e.target.value)}
                        disabled={isReadOnly || true}
                        title="Rating cannot be edited - used for content consistency"
                      >
                        <option value="U">U</option>
                        <option value="U/A">U/A</option>
                        <option value="A">A</option>
                        <option value="S">S</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Language</Form.Label>
                      {/* Language field disabled - used for content consistency */}
                      <Form.Select
                        value={formData.language}
                        onChange={(e) => handleInputChange('language', e.target.value)}
                        disabled={isReadOnly || true}
                        title="Language cannot be edited - used for content consistency"
                      >
                        <option value="English">English</option>
                        <option value="Malayalam">Malayalam</option>
                        <option value="Hindi">Hindi</option>
                        <option value="Tamil">Tamil</option>
                        <option value="Telugu">Telugu</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label="Active (visible to users on website)"
                        checked={formData.isActive}
                        onChange={(e) => handleInputChange('isActive', e.target.checked)}
                        disabled={isReadOnly}
                      />
                      <Form.Text className="text-muted">
                        Uncheck this to hide the movie from the website while keeping it in the database.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">


                      <Form.Label>Release Date</Form.Label>



                      <Form.Control
                        type="date"
                        value={formData.releaseDate}
                        onChange={(e) => {
                          const value = e.target.value;
                          const future = isFutureDate(value);

                          setFormData(prev => ({
                            ...prev,
                            releaseDate: value,
                            status: future ? 'Coming Soon' : prev.status
                          }));
                        }}
                        isInvalid={!!errors.releaseDate}
                        readOnly={isReadOnly}
                        disabled={true}
                        title="Release date cannot be edited - used for content consistency"
                      />






                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Director</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.director}
                        onChange={(e) => handleInputChange('director', e.target.value)}
                        placeholder="Director name"
                        readOnly={isReadOnly}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Cast</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.cast}
                    onChange={(e) => handleInputChange('cast', e.target.value)}
                    placeholder="Actor 1, Actor 2, Actor 3"
                    readOnly={isReadOnly}
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="media" title="Media & Links">
            <Card className="mt-3">
              <Card.Header>
                <h5 className="mb-0">Media & Links</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Poster URL</Form.Label>
                      <Form.Text className="text-muted d-block mb-2">
                        {IMAGE_SPECS.moviePoster.hint}
                      </Form.Text>
                      <div className="d-flex gap-2">
                        <Form.Control
                          type="url"
                          value={formData.posterUrl}
                          onChange={(e) => handleInputChange('posterUrl', e.target.value)}
                          placeholder="https://example.com/poster.jpg"
                          readOnly={isReadOnly}
                        />
                        {!isReadOnly && (
                          <>
                            <ImageUploadField
                              spec={IMAGE_SPECS.moviePoster}
                              onUpload={uploadPoster}
                              onChange={(url) => handleInputChange('posterUrl', url)}
                              disabled={uploadingPoster}
                              fieldId="poster-upload-input"
                              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                              showPreview={false}
                            />
                            <Button variant="outline-secondary" onClick={generatePosterUrl} title="Generate random URL">
                              <i className="bi-shuffle"></i>
                            </Button>
                          </>
                        )}
                      </div>
                      {uploadingPoster && (
                        <div className="mt-2">
                          <small className="text-muted">
                            <i className="bi-hourglass-split me-1"></i>
                            Uploading...
                          </small>
                        </div>
                      )}
                      {formData.posterUrl && (
                        <div className="mt-2">
                          <Image
                            src={formData.posterUrl}
                            alt="Movie poster"
                            style={{ maxWidth: '150px', maxHeight: '200px' }}
                            className="img-thumbnail"
                            onError={(e: any) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Trailer URL</Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Control
                          type="url"
                          value={formData.trailerUrl}
                          onChange={(e) => handleInputChange('trailerUrl', e.target.value)}
                          placeholder="https://youtube.com/watch?v=..."
                          readOnly={isReadOnly}
                        />
                        {!isReadOnly && (
                          <Button variant="outline-secondary" onClick={generateTrailerUrl}>
                            <i className="bi-shuffle"></i>
                          </Button>
                        )}
                      </div>
                      {formData.trailerUrl && (
                        <div className="mt-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => window.open(formData.trailerUrl, '_blank')}
                          >
                            <i className="bi-play-circle me-1"></i>
                            Watch Trailer
                          </Button>
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="technical" title="Technical Details">
            <Card className="mt-3">
              <Card.Header>
                <h5 className="mb-0">Technical Specifications</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Runtime (in minutes)</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.duration}
                        onChange={(e) => handleInputChange('duration', e.target.value)}
                        placeholder="135"
                        readOnly={isReadOnly}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Aspect Ratio</Form.Label>
                      <Form.Select
                        value={formData.aspectRatio}
                        onChange={(e) => handleInputChange('aspectRatio', e.target.value)}
                        disabled={isReadOnly}
                      >
                        <option value="16:9">16:9</option>
                        <option value="21:9">21:9</option>
                        <option value="4:3">4:3</option>
                        <option value="1.85:1">1.85:1</option>
                        <option value="2.35:1">2.35:1</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Color</Form.Label>
                      <Form.Select
                        value={formData.color}
                        onChange={(e) => handleInputChange('color', e.target.value)}
                        disabled={isReadOnly}
                      >
                        <option value="Color">Color</option>
                        <option value="Black and White">Black and White</option>
                        <option value="Colorized">Colorized</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Sound Mix</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.soundMix}
                        onChange={(e) => handleInputChange('soundMix', e.target.value)}
                        placeholder="Dolby Digital, DTS, etc."
                        readOnly={isReadOnly}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Age Restriction</Form.Label>
                      <Form.Select
                        value={formData.ageRestriction}
                        onChange={(e) => handleInputChange('ageRestriction', e.target.value)}
                        disabled={isReadOnly}
                      >
                        <option value="U">U - Universal</option>
                        <option value="U/A">U/A - Parental Guidance</option>
                        <option value="A">A - Adults Only</option>
                        <option value="S">S - Restricted</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label="Subtitles Available"
                        checked={formData.subtitles}
                        onChange={(e) => handleInputChange('subtitles', e.target.checked)}
                        disabled={isReadOnly}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label="Dubbing Available"
                        checked={formData.dubbing}
                        onChange={(e) => handleInputChange('dubbing', e.target.checked)}
                        disabled={isReadOnly}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="crew" title="Cast & Crew">
            <Card className="mt-3">
              <Card.Header>
                <h5 className="mb-0">Cast & Crew Details</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Cinematography</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.cinematography}
                        onChange={(e) => handleInputChange('cinematography', e.target.value)}
                        placeholder="Cinematographer name"
                        readOnly={isReadOnly}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Editing</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.editing}
                        onChange={(e) => handleInputChange('editing', e.target.value)}
                        placeholder="Editor name"
                        readOnly={isReadOnly}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Music</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.music}
                        onChange={(e) => handleInputChange('music', e.target.value)}
                        placeholder="Music director/composer"
                        readOnly={isReadOnly}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Production Design</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.productionDesign}
                        onChange={(e) => handleInputChange('productionDesign', e.target.value)}
                        placeholder="Production designer"
                        readOnly={isReadOnly}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Costume Design</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.costumeDesign}
                        onChange={(e) => handleInputChange('costumeDesign', e.target.value)}
                        placeholder="Costume designer"
                        readOnly={isReadOnly}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Makeup</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.makeup}
                        onChange={(e) => handleInputChange('makeup', e.target.value)}
                        placeholder="Makeup artist"
                        readOnly={isReadOnly}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Special Effects</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.specialEffects}
                        onChange={(e) => handleInputChange('specialEffects', e.target.value)}
                        placeholder="VFX/SFX team"
                        readOnly={isReadOnly}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Stunts</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.stunts}
                        onChange={(e) => handleInputChange('stunts', e.target.value)}
                        placeholder="Stunt coordinator"
                        readOnly={isReadOnly}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Other Crew</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.otherCrew}
                    onChange={(e) => handleInputChange('otherCrew', e.target.value)}
                    placeholder="Additional crew members and their roles"
                    readOnly={isReadOnly}
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="ratings" title="Ratings & Awards">
            <Card className="mt-3">
              <Card.Header>
                <h5 className="mb-0">Ratings & Awards</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>IMDb Rating</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.imdbRating}
                        onChange={(e) => handleInputChange('imdbRating', e.target.value)}
                        placeholder="8.5/10"
                        readOnly={isReadOnly}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Rotten Tomatoes</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.rottenTomatoesRating}
                        onChange={(e) => handleInputChange('rottenTomatoesRating', e.target.value)}
                        placeholder="85%"
                        readOnly={isReadOnly}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Budget</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.budget}
                        onChange={(e) => handleInputChange('budget', e.target.value)}
                        placeholder="$50M"
                        readOnly={isReadOnly}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Box Office</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.boxOffice}
                        onChange={(e) => handleInputChange('boxOffice', e.target.value)}
                        placeholder="$500M worldwide"
                        readOnly={isReadOnly}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Awards</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.awards}
                        onChange={(e) => handleInputChange('awards', e.target.value)}
                        placeholder="Oscar Winner, Golden Globe, etc."
                        readOnly={isReadOnly}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="seo" title="SEO & Meta">
            <Card className="mt-3">
              <Card.Header>
                <h5 className="mb-0">SEO & Meta Information</h5>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Meta Title</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.metaTitle}
                    onChange={(e) => handleInputChange('metaTitle', e.target.value)}
                    placeholder="Movie Title - MyCinemas"
                    readOnly={isReadOnly}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Meta Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.metaDescription}
                    onChange={(e) => handleInputChange('metaDescription', e.target.value)}
                    placeholder="Brief description for search engines"
                    readOnly={isReadOnly}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Tags</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.tags}
                    onChange={(e) => handleInputChange('tags', e.target.value)}
                    placeholder="action, thriller, 2024, blockbuster"
                    readOnly={isReadOnly}
                  />
                  <Form.Text className="text-muted">
                    Separate tags with commas
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {isReadOnly ? 'Close' : 'Cancel'}
        </Button>
        {!isReadOnly && (
          <Button variant="primary" onClick={handleSave} disabled={isLoading}>
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              <>
                <i className="bi-save me-1"></i>
                {mode === 'add' ? 'Create Movie' : 'Save Changes'}
              </>
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default MovieDetailsModal;
